export const SUPPORTED_LANGUAGES = {
  en: { name: 'English', flag: '🇬🇧' },
  'es-419': { name: 'Español (LATAM)', flag: '🌎' },
  'pt-BR': { name: 'Português (BR)', flag: '🇧🇷' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  fr: { name: 'Français', flag: '🇫🇷' },
  nl: { name: 'Nederlands', flag: '🇳🇱' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  uk: { name: 'Українська', flag: '🇺🇦' }
} as const;

export type SupportedLanguage = keyof typeof SUPPORTED_LANGUAGES;

// ✅ Direct static imports for reliable translation loading
import enTranslations from '../locales/en.json';
import es419Translations from '../locales/es-419.json';
import ptBRTranslations from '../locales/pt-BR.json';
import deTranslations from '../locales/de.json';
import frTranslations from '../locales/fr.json';
import nlTranslations from '../locales/nl.json';
import itTranslations from '../locales/it.json';
import ukTranslations from '../locales/uk.json';

// Build translations map from direct imports
const TRANSLATIONS_MAP: Record<SupportedLanguage, Record<string, any>> = {
  en: enTranslations,
  'es-419': es419Translations,
  'pt-BR': ptBRTranslations,
  de: deTranslations,
  fr: frTranslations,
  nl: nlTranslations,
  it: itTranslations,
  uk: ukTranslations
};

// ✅ Normalize language codes (handles browser variants)
export const normalizeLanguage = (rawLang?: string): SupportedLanguage => {
  if (!rawLang) return 'en';
  
  const normalized = rawLang.toLowerCase().trim();
  
  // Direct matches with fallbacks
  if (normalized === 'uk' || normalized === 'ua' || normalized.startsWith('uk-')) return 'uk';
  if (normalized === 'pt-br' || normalized.startsWith('pt-br')) return 'pt-BR';
  if (normalized === 'pt' || normalized.startsWith('pt')) return 'pt-BR';
  if (normalized === 'es-419' || normalized.startsWith('es-419')) return 'es-419';
  if (normalized === 'es' || normalized.startsWith('es')) return 'es-419';
  if (normalized === 'de' || normalized.startsWith('de')) return 'de';
  if (normalized === 'fr' || normalized.startsWith('fr')) return 'fr';
  if (normalized === 'nl' || normalized.startsWith('nl')) return 'nl';
  if (normalized === 'it' || normalized.startsWith('it')) return 'it';
  if (normalized === 'en' || normalized.startsWith('en')) return 'en';
  
  return 'en';
};

// Device language detection with normalization
export const detectDeviceLanguage = (): SupportedLanguage => {
  const deviceLang = navigator.language || (navigator.languages && navigator.languages[0]) || 'en';
  return normalizeLanguage(deviceLang);
};

/**
 * Get nested value from object using dot notation
 * e.g., getNestedValue({ pwa: { install: { title: "..." } } }, "pwa.install.title") → "..."
 */
const getNestedValue = (obj: Record<string, any>, path: string): string | undefined => {
  const keys = path.split('.');
  let current: any = obj;
  
  for (const key of keys) {
    if (current === undefined || current === null || typeof current !== 'object') {
      return undefined;
    }
    current = current[key];
  }
  
  return typeof current === 'string' ? current : undefined;
};

// ✅ Synchronous translator creation (no async race conditions)
export const createTranslator = (lang: SupportedLanguage) => {
  const normalizedLang = normalizeLanguage(lang);
  
  // Fallback chain: exact → en
  const translations = TRANSLATIONS_MAP[normalizedLang] || TRANSLATIONS_MAP['en'];
  
  return (key: string, variables?: Record<string, any>): string => {
    // Try nested lookup first, then fallback to flat key, then English, then key itself
    let text = 
      getNestedValue(translations, key) || 
      translations[key] || 
      getNestedValue(TRANSLATIONS_MAP['en'], key) || 
      TRANSLATIONS_MAP['en'][key] || 
      key;
    
    // Replace variables like {name}, {amount}
    if (variables) {
      Object.entries(variables).forEach(([varKey, value]) => {
        text = text.replace(new RegExp(`\\{${varKey}\\}`, 'g'), String(value));
      });
    }
    
    return text;
  };
};

// Helper: Get available translations count for debugging
export const getTranslationsCount = (lang: SupportedLanguage): number => {
  return Object.keys(TRANSLATIONS_MAP[lang] || {}).length;
};
